import React, { useEffect, useMemo, useState } from "react";
import { getWheelConfig, saveWheelConfig, DEFAULT_WHEEL_CONFIG } from "../../services/wheel";
import { Plus, Trash2, Save, Loader2, Check, Disc3, Percent, Truck, Gift, XCircle, Info } from "lucide-react";

const TYPES = [
  {
    v: "coupon_percent",
    label: "Faiz Endirim Kuponu",
    icon: Percent,
    color: "#c53a24",
    hint: "Müştəri qazandıqda kod avtomatik yaradılır (ör: WHEEL10-ABC123). Növbəti sifarişdə səbətdə tətbiq olunur. Faizi aşağıda təyin edin.",
    usesPercent: true,
  },
  {
    v: "free_shipping",
    label: "Pulsuz Çatdırılma",
    icon: Truck,
    color: "#2e7ec7",
    hint: "Kod (ör: SHIP-ABC123) yaradılır və növbəti sifarişdə çatdırılma üstləyini sıfırlayır.",
    usesPercent: false,
  },
  {
    v: "gift_item",
    label: "Fiziki Hədiyyə",
    icon: Gift,
    color: "#c98b4b",
    hint: "Komanda əl ilə göndərir (məs: vinyl poster, sticker, tote bag). Sifarişə əlaqələndirilir.",
    usesPercent: false,
  },
  {
    v: "empty",
    label: "Boş (Uduzma)",
    icon: XCircle,
    color: "#7a7a7a",
    hint: "Hədiyyə verilmir. Ehtimalı artırmaq üçün çəkisini yüksəldin.",
    usesPercent: false,
  },
];

const TYPE_MAP = Object.fromEntries(TYPES.map((t) => [t.v, t]));

export default function AdminWheel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => { getWheelConfig().then(setCfg); }, []);

  const totalWeight = useMemo(() => {
    if (!cfg?.slices) return 0;
    return cfg.slices.reduce((s, x) => s + (Number(x.weight) || 1), 0);
  }, [cfg]);

  if (!cfg) return <div className="p-10 text-sm text-[var(--text-muted)]">Yüklənir…</div>;

  const setField = (k, v) => setCfg((s) => ({ ...s, [k]: v }));
  const setSlice = (i, k, v) => setCfg((s) => ({
    ...s, slices: s.slices.map((x, ix) => ix === i ? { ...x, [k]: v } : x),
  }));
  const addSlice = () => setCfg((s) => ({
    ...s,
    slices: [...s.slices, { number: s.slices.length + 1, label: "", type: "empty", percent: 0, weight: 1 }],
  }));
  const delSlice = (i) => setCfg((s) => ({ ...s, slices: s.slices.filter((_, ix) => ix !== i) }));
  const resetDefault = () => setCfg({ ...DEFAULT_WHEEL_CONFIG });

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await saveWheelConfig(cfg);
      setSavedAt(Date.now());
    } catch (e) {
      setErr(e.message || "Səhv baş verdi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 md:p-10" data-testid="admin-wheel">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium flex items-center gap-2">
            <Disc3 size={12} className="text-[var(--accent)]" /> Satış
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl mt-1">Şans Çarxı</h1>
          <div className="text-xs text-[var(--text-muted)] mt-1">Dilimləri və hədiyyə tiplərini idarə edin</div>
        </div>
        <div className="flex gap-2">
          <button onClick={resetDefault} className="text-xs px-3 py-2 border border-[var(--border)] hover:border-[var(--accent)]">Standart dəyərlər</button>
          <button onClick={save} disabled={saving} className="btn-primary" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="admin-wheel-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Yadda saxla
          </button>
        </div>
      </div>

      {savedAt > 0 && (
        <div className="border border-green-500/30 bg-green-500/5 text-green-500 p-3 text-sm mb-4 flex items-center gap-2">
          <Check size={14} /> Yadda saxlandı. Dəyişikliklər dərhal aktivdir.
        </div>
      )}
      {err && <div className="border border-[var(--accent)]/30 bg-[var(--accent)]/5 text-[var(--accent)] p-3 text-sm mb-4">{err}</div>}

      {/* Config strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <label className="block">
          <span className="text-[11px] text-[var(--text-muted)] font-medium block mb-1">Çarx aktivdir?</span>
          <select value={cfg.enabled ? "1" : "0"} onChange={(e) => setField("enabled", e.target.value === "1")} className="input" data-testid="admin-wheel-enabled">
            <option value="1">Bəli — istifadəçilər fırlada bilər</option>
            <option value="0">Xeyr — gizli</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] text-[var(--text-muted)] font-medium block mb-1">Min. sifariş məbləği (AZN)</span>
          <input type="number" step="1" value={cfg.min_order_azn} onChange={(e) => setField("min_order_azn", e.target.value)} className="input" data-testid="admin-wheel-minorder" />
        </label>
        <div className="block">
          <span className="text-[11px] text-[var(--text-muted)] font-medium block mb-1">Cəmi dilim / ümumi çəki</span>
          <div className="input flex items-center justify-between">
            <span><span className="text-[var(--text)]">{cfg.slices.length}</span> dilim · <span className="text-[var(--text)]">{totalWeight}</span> çəki</span>
            <button onClick={addSlice} className="text-[var(--accent)] text-sm inline-flex items-center gap-1" data-testid="admin-wheel-add">
              <Plus size={12} /> ƌlavə et
            </button>
          </div>
        </div>
      </div>

      {/* Type reference legend */}
      <div className="border border-[var(--border)] bg-[var(--surface)]/40 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium">
          <Info size={14} className="text-[var(--accent)]" /> Hədiyyə Tiplərinin İşləyişi
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.v} className="flex items-start gap-3 text-xs">
                <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: `${t.color}22`, color: t.color }}>
                  <Icon size={14} />
                </div>
                <div>
                  <div className="font-medium text-sm" style={{ color: t.color }}>{t.label}</div>
                  <div className="text-[var(--text-muted)] mt-0.5">{t.hint}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Slices editor */}
      <div className="space-y-3">
        <div className="text-sm font-medium">Dilim Hədiyyələri</div>
        <div className="space-y-2">
          {cfg.slices.map((s, i) => {
            const t = TYPE_MAP[s.type] || TYPE_MAP.empty;
            const Icon = t.icon;
            const chance = totalWeight ? ((Number(s.weight) || 1) / totalWeight * 100).toFixed(1) : 0;
            return (
              <div key={i} className="border border-[var(--border)] bg-[var(--surface)]/40 p-3" data-testid={`admin-wheel-slice-${i}`}>
                <div className="grid grid-cols-12 gap-2 items-center">
                  {/* Type icon indicator (visual) */}
                  <div className="col-span-2 sm:col-span-1">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto" style={{ background: `${t.color}22`, color: t.color }}>
                      <Icon size={16} />
                    </div>
                  </div>
                  {/* Number */}
                  <div className="col-span-2 sm:col-span-1">
                    <input type="number" value={s.number} onChange={(e) => setSlice(i, "number", e.target.value)} className="input text-center" placeholder="#" title="Rəqəm" />
                  </div>
                  {/* Label */}
                  <div className="col-span-8 sm:col-span-3">
                    <input value={s.label} onChange={(e) => setSlice(i, "label", e.target.value)} className="input" placeholder="Məs: 10% Endirim" data-testid={`admin-wheel-slice-${i}-label`} />
                  </div>
                  {/* Type */}
                  <div className="col-span-6 sm:col-span-3">
                    <select value={s.type} onChange={(e) => setSlice(i, "type", e.target.value)} className="input">
                      {TYPES.map((tt) => <option key={tt.v} value={tt.v}>{tt.label}</option>)}
                    </select>
                  </div>
                  {/* Percent (only if applicable) */}
                  <div className="col-span-3 sm:col-span-1">
                    <input type="number" value={s.percent} onChange={(e) => setSlice(i, "percent", e.target.value)} className={`input ${!t.usesPercent ? "opacity-40" : ""}`} placeholder="%" title="Faiz" disabled={!t.usesPercent} />
                  </div>
                  {/* Weight */}
                  <div className="col-span-2 sm:col-span-1">
                    <input type="number" min="1" value={s.weight} onChange={(e) => setSlice(i, "weight", e.target.value)} className="input text-center" placeholder="Çəki" title="Çəki" />
                  </div>
                  {/* Chance % */}
                  <div className="col-span-6 sm:col-span-1 text-center">
                    <span className="text-[11px] text-[var(--text-muted)]">şans:</span>
                    <div className="font-medium text-sm" style={{ color: t.color }}>{chance}%</div>
                  </div>
                  {/* Delete */}
                  <div className="col-span-1 flex justify-end">
                    <button onClick={() => delSlice(i)} className="text-[var(--accent)] hover:text-red-600 p-1" title="Sil"><Trash2 size={14} /></button>
                  </div>
                </div>
                {/* Header labels (only on first row) */}
                {i === 0 && (
                  <div className="hidden sm:grid grid-cols-12 gap-2 text-[9px] tracking-[0.14em] text-[var(--text-muted)] mt-2 pt-2 border-t border-[var(--border)]/50">
                    <div className="col-span-1 text-center">TİP</div>
                    <div className="col-span-1 text-center">RƌQƌM</div>
                    <div className="col-span-3">AD</div>
                    <div className="col-span-3">FUNKSİYA</div>
                    <div className="col-span-1 text-center">FAİZ</div>
                    <div className="col-span-1 text-center">ÇƌKİ</div>
                    <div className="col-span-1 text-center">ŞANS</div>
                    <div className="col-span-1"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="text-[11px] text-[var(--text-muted)]">
          • <b>Çəki</b>: yüksək çəkili dilim daha tez-tez qazanılır. Məsələn: boş dilimin çəkisini 5 etsəniz və 20% endirimin çəkisini 1 etsəniz — 5:1 nisbəti.
          <br />
          • <b>Faiz</b>: yalnız "Faiz Endirim Kuponu" tipi üçün işləyir.
        </div>
      </div>
    </div>
  );
}
